<template>
	<div :class="classes">
		<FixedPositionOverlay
			:is-visible="shouldShowAssigneePicker"
			:top="assigneeMenuData.y"
			:left="assigneeMenuData.x"
		>
			<CommitmentAssignees
				:members="teamMembers"
				:selected-member-id="patchedAssigneeMenuData?.assignedId"
				:show-unassign="canUnassignCommitment"
				@select="onAssigneeSelected"
			/>
		</FixedPositionOverlay>
		<FixedPositionOverlay
			:is-visible="shouldShowCalendarPicker"
			:top="calendarMenuData.y"
			:left="calendarMenuData.x"
		>
			<CommitmentCalendar
				:value="calendarMenuData?.dateValue"
				@setDate="onSetDueDate"
				@cancel="onCancelCalendar"
				@removeDate="onRemoveDueDate"
			/>
		</FixedPositionOverlay>
		<LiteScroller
			class="commitment-list-scroller"
			:docked="true"
			:is-paging="isPaging"
			:is-paging-enabled="hasMorePages"
			@scroll="onScroll"
			@page="onScrollThresholdReached"
		>
			<template #default>
				<div class="commitment-list__rows">
					<CommitmentRow
						v-for="commitment in commitments" :key="commitment.id"
						class="commitment-list__commitment-row"
						:can-change-assignee="canChangeAssignee"
						:can-change-commitment-content="!readonlyFields.includes( fields.CONTENT )"
						:can-complete-commitment="!readonlyFields.includes( fields.COMPLETION )"
						:commitment="commitment"
						:is-assignee-menu-open="commitment.id === assigneeMenuData.idWithMenuOpen"
						:is-being-edited="commitment.isBeingEdited"
						:is-saving="commitment.isSaving"
						:outcome-display-location="outcomeDisplayLocation"
						:team-members="teamMembers"
						@assigneeMenuOpen="onAssigneeMenuToggle"
						@calendarPickerOpen="onCalendarPickerOpen"
						@check="onCommitmentRowCheck( commitment, $event )"
						@commitmentTextChange="onCommitmentTextChange"
						@commitmentTextEditCancel="onCommitmentTextEditCancel"
						@commitmentTextEnterKey="onCommitmentTextEnterKey"
						@contextClick="onCommitmentContextClick"
						@delete="onCommitmentDelete"
						@editText="onCommitmentEditText"
						@save="onSaveNewCommitment"
					/>
				</div>
			</template>
		</LiteScroller>
	</div>
</template>

<script>
import moment from "moment-timezone";

import {
	OutcomeDisplayLocation,
	OutcomeType
} from "@teaming/common-enums";

import DebugMixin from "../../mixins/DebugMixin";
import CommitmentAssignees from "./CommitmentAssignees";
import CommitmentCalendar from "./CommitmentCalendar";
import CommitmentRow, { CommitmentRowFields } from "./CommitmentRow";
import FixedPositionOverlay from "../FixedPositionOverlay";
import LiteScroller from "../LiteScroller.vue";

function getPristineAssigneeMenuState() {
	return {
		x: 0,
		y: 0,
		width: 0,
		assignedId: undefined,
		commitment: undefined,
	};
}

function getPristineCalendarMenuState() {
	return {
		x: 0,
		y: 0,
		width: 0,
		assignedId: undefined,
		commitment: undefined,
	};
}

export default {
	name: "CommitmentList",
	teamingPackage: "common-ui",
	mixins: [ DebugMixin, ],
	emits: [
		"assigneeSelect",
		"clickOutside",
		"commitmentDelete",
		"commitmentEditText",
		"commitmentTextChange",
		"commitmentTextEditCancel",
		"commitmentTextEnterKey",
		"contextClick",
		"dueDateChange",
		"endReached",
		"patch",
		"saveNewCommitment",
	],
	components: {
		CommitmentAssignees,
		CommitmentCalendar,
		CommitmentRow,
		FixedPositionOverlay,
		LiteScroller,
	},
	data() {
		return {
			clickOutsideHandler: null,
			globalClickHandler: null,
			assigneeMenuData: getPristineAssigneeMenuState(),
			calendarMenuData: getPristineCalendarMenuState(),
		};
	},
	props: {
		canChangeAssignee: {
			type: Boolean,
			default: false,
		},
		className: {
			type: String,
			default: "",
		},
		commitments: {
			type: Array,
			required: true,
		},
		defaultAssigneeId: {
			type: String,
			default: "",
		},
		outcomeDisplayLocation: {
			type: String,
			default: OutcomeDisplayLocation.TEAM_SPACE,
			validator( type ) {
				return OutcomeDisplayLocation.includes( type );
			},
		},
		isPaging: {
			type: Boolean,
			required: true,
		},
		hasMorePages: {
			type: Boolean,
			required: true,
		},
		readonlyFields: {
			type: Array,
			default: () => [],
		},
		teamMembers: {
			type: Array,
			default: () => ( [] ),
		},
	},
	computed: {
		classes() {
			return {
				"commitment-list": true,
				[ this.className ]: !!this.className,
			};
		},
		fields() {
			return CommitmentRowFields;
		},
		patchedAssigneeMenuData() {
			if ( this.assigneeMenuData?.assignedId ) {
				return this.assigneeMenuData;
			}
			return {
				...this.assigneeMenuData,
				assignedId: this.defaultAssigneeId,
			};
		},
		shouldShowAssigneePicker() {
			return this.canChangeAssignee && !!this.assigneeMenuData?.commitment;
		},
		shouldShowCalendarPicker() {
			return !!this.calendarMenuData?.commitment;
		},
		canUnassignCommitment() {
			return this.assigneeMenuData.commitment?.type !== OutcomeType.PERSONAL_ACTION_ITEM;
		},
	},
	methods: {
		onAssigneeMenuToggle( { commitment, event: e, assignedId, } ) {
			const isSameCommitment = commitment.id === this.assigneeMenuData.commitment?.id;
			if ( isSameCommitment ) {
				this.assigneeMenuData = getPristineAssigneeMenuState();
				return;
			}
			this.assigneeMenuData = {
				x: e.clientX,
				y: e.clientY,
				width: e.srcElement.width,
				assignedId,
				commitment,
			};
		},
		onCalendarPickerOpen( { commitment, event: e, dateValue, } ) {
			const isSameCommitment = commitment.id === this.calendarMenuData.commitment?.id;
			if ( isSameCommitment ) {
				this.calendarMenuData = getPristineCalendarMenuState();
				return;
			}
			this.calendarMenuData = {
				x: e.clientX,
				y: e.clientY,
				width: e.srcElement.width,
				dateValue,
				commitment,
			};
		},
		async onAssigneeSelected( member ) {
			await this.$nextTick();
			const { assigneeMenuData, } = this;
			this.debug( { member, assigneeMenuData, } );
			this.$emit( "assigneeSelect", {
				member,
				commitment: assigneeMenuData.commitment,
			} );
		},
		onCancelCalendar() {
			this.calendarMenuData = getPristineCalendarMenuState();
		},
		onCommitmentContextClick( data ) {
			this.$emit( "contextClick", data );
		},
		onCommitmentRowCheck( commitment, isChecked ) {
			this.$emit( "patch", {
				commitment,
				patches: [
					{
						completed: isChecked ? moment() : null,
						isCompleted: isChecked,
					},
				],
			} );
		},
		onCommitmentDelete( commitment ) {
			this.$emit( "commitmentDelete", commitment );
		},
		onCommitmentEditText( { id, content, } ) {
			this.$emit( "commitmentEditText", { id, content, } );
		},
		onCommitmentTextChange( { id, content, isNewCommitment, } ) {
			this.$emit( "commitmentTextChange", { id, content, isNewCommitment, } );
		},
		onCommitmentTextEditCancel( { commitment, } ) {
			this.$emit( "commitmentTextEditCancel", { commitment, } );
		},
		onCommitmentTextEnterKey( { commitment, } ) {
			this.$emit( "commitmentTextEnterKey", { commitment, } );
		},
		onRemoveDueDate() {
			this.$emit( "dueDateChange", { dueDate: null, commitment: this.calendarMenuData.commitment, } );
		},
		onSaveNewCommitment( commitment ) {
			if ( !commitment.isNewCommitment ) {
				return;
			}
			this.$emit( "saveNewCommitment", commitment );
		},
		onScroll() {
			this.calendarMenuData = getPristineCalendarMenuState();
			this.assigneeMenuData = getPristineAssigneeMenuState();
		},
		onScrollThresholdReached() {
			this.$emit( "endReached" );
		},
		onSetDueDate( date ) {
			const dueDate = moment( date );
			this.$emit( "dueDateChange", { dueDate, commitment: this.calendarMenuData.commitment, } );
		},
	},
	watch: {
		shouldShowAssigneePicker( newVal, oldVal ) {
			if ( !oldVal && newVal ) {
				this.calendarMenuData = getPristineCalendarMenuState();
			}
		},
		shouldShowCalendarPicker( newVal, oldVal ) {
			if ( !oldVal && newVal ) {
				this.assigneeMenuData = getPristineAssigneeMenuState();
			}
		},
	},
	created() {
		const { global, } = this.$foundation();
		this.clickOutsideHandler = global.onClickOutside( "commitment-list__rows", async () => {
			this.$emit( "clickOutside" );
		} );
	},
	mounted() {
		// @deprecated: prefer foundation.global.onClickOutside() instead
		this.globalClickHandler = e => {
			const path = e.path || ( e.composedPath && e.composedPath() );
			if ( !path ) {
				return;
			}
			const paths = path.reduce( ( acc, element ) => {
				if ( element.classList ) {
					element.classList.forEach( x => acc.add( x ) );
				}
				return acc;
			}, new Set() );
			if ( !paths.has( "commitment-row__assignee-column" ) ) {
				this.assigneeMenuData = getPristineAssigneeMenuState();
			}
			if (
				!paths.has( "commitment-row__due-column" ) &&
				!paths.has( "p-datepicker-prev" ) &&
				!paths.has( "p-datepicker-next" )
			) {
				this.calendarMenuData = getPristineCalendarMenuState();
			}
		};
		window.addEventListener( "click", this.globalClickHandler );
	},
	beforeUnmount() {
		const { global, } = this.$foundation();
		global.offClickOutside( this.clickOutsideHandler );
		window.removeEventListener( "click", this.globalClickHandler );
	},
};
</script>

<style lang="scss">
.commitment-list {
	.p-datepicker {
		border: 0.0625rem solid $commitment-list-calendar-picker-border-color;
		box-shadow: 0rem 0.375rem 0.375rem 0.0625rem rgb(33 45 99 / 10%),
			0rem 0.3125rem 0.875rem -1.25rem rgb(33 45 99 / 20%);
	}
}

.commitment-list-scroller {
	.commitment-row__highlight-wrapper--is-editing {
		margin: 0.1875rem;
	}
}
</style>

<style lang="scss" scoped>
.commitment-list {
	position: relative;
	width: 100%;
	height: 100%;
}
</style>
